/**
 * useFavourites — React Query hook for the engineer's favourites list.
 *
 * DEV-46921. Backend-persisted (not localStorage) so favourites sync
 * across a user's devices. A favourite can target either:
 *   - a master product (canonical, cross-supplier) — heart on Search
 *     result cards + master ProductDetail; tapping the tile opens
 *     `/engineer/product/{master_product_id}`, OR
 *   - a specific supplier's raw / linked product — heart on the
 *     SupplierProductDetail page + on recently-purchased tiles; tapping
 *     the favourite tile opens `/engineer/supplier-product/{raw_product_id}`.
 *
 * Provides:
 *   - `favourites` — full list with tile-ready best listings for the
 *     caller's current postcode.
 *   - `favouriteMasterIds` / `favouriteRawIds` — Sets for cheap heart-
 *     icon hydration across Search / ProductDetail / SupplierProductDetail.
 *   - `isFavourited({ type, id })` — synchronous check using the Sets.
 *   - `addFavouriteAsync` / `removeFavouriteAsync` / `restoreFavouriteAsync`
 *     — mutations with an optimistic id-set update so hearts flip
 *     instantly. Resolve with the saved favourite / what was removed, so
 *     the caller can offer "Add to list" and Undo. Use
 *     `components/engineer/favourites/useFavouriteToggle` for the heart
 *     itself — it owns the toasts (DEV-51010).
 *   - `addFavourite` / `removeFavourite` — fire-and-forget versions.
 *
 * Un-hearting also takes the favourite out of every list, so the remove
 * and restore mutations keep the favourite-lists cache in step.
 */

import { useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'
import { api } from '../../services/api'
import { HOME_TAB_ENABLED } from '../../config/features'
import { favouriteListsQueryKey } from './useFavouriteLists'
import type { Favourite, FavouriteList, FavouriteTargetType } from '../../types'

interface EngineerSession {
  postcode: string
  latitude: number
  longitude: number
}

interface FavouriteIdSets {
  masterProductIds: number[]
  rawProductIds: number[]
}

export interface FavouriteTarget {
  type: FavouriteTargetType
  id: number | string
}

function readSession(): EngineerSession | null {
  try {
    const raw = sessionStorage.getItem('engineerSession')
    return raw ? (JSON.parse(raw) as EngineerSession) : null
  } catch {
    return null
  }
}

function includesId(list: number[], id: number): boolean {
  return list.includes(id)
}

function withoutId(list: number[], id: number): number[] {
  return list.filter((x) => x !== id)
}

function withTargetId(ids: FavouriteIdSets, target: FavouriteTarget): FavouriteIdSets {
  const id = Number(target.id)
  return target.type === 'master'
    ? {
        ...ids,
        masterProductIds: includesId(ids.masterProductIds, id)
          ? ids.masterProductIds
          : [...ids.masterProductIds, id],
      }
    : {
        ...ids,
        rawProductIds: includesId(ids.rawProductIds, id)
          ? ids.rawProductIds
          : [...ids.rawProductIds, id],
      }
}

export function useFavourites() {
  const queryClient = useQueryClient()
  const { tenantSlug } = useParams<{ tenantSlug: string }>()
  const scope = tenantSlug || 'default'

  const listKey = ['favourites', scope]
  const idsKey = ['favourite-ids', scope]
  const listsKey = favouriteListsQueryKey(scope)

  // Full list — resolved with the current postcode so tiles show the
  // right supplier/branch/price/stock. Refreshed on window focus so a
  // change made on another device shows up when the engineer returns.
  //
  // `enabled: HOME_TAB_ENABLED` disables the network call when the
  // build flag is off. Callers like SearchProductCard mount on pages
  // that stay reachable even when the feature is hidden — without
  // this guard those cards would still fire GET /api/favourites/*
  // requests to the backend on every render. With the guard the hook
  // returns empty data and never hits the network.
  const listQuery = useQuery<Favourite[]>({
    queryKey: listKey,
    queryFn: () => {
      const session = readSession()
      return api.listFavourites(
        session?.postcode,
        session?.latitude,
        session?.longitude,
      )
    },
    staleTime: 60 * 1000, // 1 min — tile prices are stored-not-live
    enabled: HOME_TAB_ENABLED,
  })

  // Two lightweight id sets for heart hydration. Kept separate from the
  // list query so Search / ProductDetail / SupplierProductDetail pages
  // don't pay for tile resolution. Same `enabled` guard as above so
  // heart-icon hydration is silent when the feature is off.
  const idsQuery = useQuery<FavouriteIdSets>({
    queryKey: idsKey,
    queryFn: () => api.listFavouriteIds(),
    staleTime: 5 * 60 * 1000, // 5 min — flipped locally by mutations
    enabled: HOME_TAB_ENABLED,
  })

  const masterIdSet = useMemo(
    () => new Set<number>(idsQuery.data?.masterProductIds ?? []),
    [idsQuery.data?.masterProductIds],
  )
  const rawIdSet = useMemo(
    () => new Set<number>(idsQuery.data?.rawProductIds ?? []),
    [idsQuery.data?.rawProductIds],
  )

  const addMutation = useMutation({
    mutationFn: (target: FavouriteTarget) => {
      const session = readSession()
      return api.addFavourite(target, {
        postcode: session?.postcode,
        latitude: session?.latitude,
        longitude: session?.longitude,
      })
    },
    onMutate: async (target: FavouriteTarget) => {
      // Flip the id set optimistically so hearts turn on instantly.
      await queryClient.cancelQueries({ queryKey: idsKey })
      const previous = queryClient.getQueryData<FavouriteIdSets>(idsKey) ?? {
        masterProductIds: [],
        rawProductIds: [],
      }
      queryClient.setQueryData<FavouriteIdSets>(idsKey, withTargetId(previous, target))
      return { previous }
    },
    onError: (_err, _target, context) => {
      if (context?.previous) {
        queryClient.setQueryData<FavouriteIdSets>(idsKey, context.previous)
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: idsKey })
      queryClient.invalidateQueries({ queryKey: listKey })
    },
  })

  const removeMutation = useMutation({
    mutationFn: (target: FavouriteTarget) => api.removeFavourite(target),
    onMutate: async (target: FavouriteTarget) => {
      await queryClient.cancelQueries({ queryKey: idsKey })
      await queryClient.cancelQueries({ queryKey: listKey })
      const previousIds = queryClient.getQueryData<FavouriteIdSets>(idsKey) ?? {
        masterProductIds: [],
        rawProductIds: [],
      }
      const previousList = queryClient.getQueryData<Favourite[]>(listKey) ?? []
      const id = Number(target.id)
      const nextIds: FavouriteIdSets =
        target.type === 'master'
          ? {
              ...previousIds,
              masterProductIds: withoutId(previousIds.masterProductIds, id),
            }
          : {
              ...previousIds,
              rawProductIds: withoutId(previousIds.rawProductIds, id),
            }
      queryClient.setQueryData<FavouriteIdSets>(idsKey, nextIds)
      queryClient.setQueryData<Favourite[]>(
        listKey,
        previousList.filter((f) =>
          target.type === 'master'
            ? f.masterProductId !== id
            : f.rawProductId !== id,
        ),
      )
      return { previousIds, previousList }
    },
    onError: (_err, _target, context) => {
      if (context?.previousIds) {
        queryClient.setQueryData<FavouriteIdSets>(idsKey, context.previousIds)
      }
      if (context?.previousList) {
        queryClient.setQueryData<Favourite[]>(listKey, context.previousList)
      }
    },
    onSuccess: (removed) => {
      // The favourite left every list with it. Counts drop straight away;
      // updatedAt stays put because un-hearting isn't a list edit.
      if (!removed) return
      queryClient.setQueryData<FavouriteList[]>(listsKey, (lists) =>
        lists?.map((list) => ({
          ...list,
          favouriteIds: list.favouriteIds.filter((id) => id !== removed.favouriteId),
        })),
      )
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: idsKey })
      queryClient.invalidateQueries({ queryKey: listKey })
    },
  })

  // Undo after un-hearting (DEV-51010). The server restores the favourite
  // and the list memberships that un-heart removed.
  const restoreMutation = useMutation({
    mutationFn: ({ favouriteId }: { favouriteId: string; target: FavouriteTarget }) => {
      const session = readSession()
      return api.restoreFavourite(favouriteId, {
        postcode: session?.postcode,
        latitude: session?.latitude,
        longitude: session?.longitude,
      })
    },
    onMutate: async ({ target }: { favouriteId: string; target: FavouriteTarget }) => {
      await queryClient.cancelQueries({ queryKey: idsKey })
      const previous = queryClient.getQueryData<FavouriteIdSets>(idsKey) ?? {
        masterProductIds: [],
        rawProductIds: [],
      }
      queryClient.setQueryData<FavouriteIdSets>(idsKey, withTargetId(previous, target))
      return { previous }
    },
    onError: (_err, _variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData<FavouriteIdSets>(idsKey, context.previous)
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: idsKey })
      queryClient.invalidateQueries({ queryKey: listKey })
      queryClient.invalidateQueries({ queryKey: listsKey })
    },
  })

  const isFavourited = (target: FavouriteTarget): boolean => {
    const id = Number(target.id)
    return target.type === 'master' ? masterIdSet.has(id) : rawIdSet.has(id)
  }

  return {
    favourites: listQuery.data ?? [],
    favouriteMasterIds: masterIdSet,
    favouriteRawIds: rawIdSet,
    isLoading: listQuery.isLoading,
    isError: listQuery.isError,
    error: listQuery.error,
    refetch: listQuery.refetch,

    isFavourited,
    addFavourite: (target: FavouriteTarget) => addMutation.mutate(target),
    removeFavourite: (target: FavouriteTarget) => removeMutation.mutate(target),
    addFavouriteAsync: (target: FavouriteTarget) => addMutation.mutateAsync(target),
    removeFavouriteAsync: (target: FavouriteTarget) => removeMutation.mutateAsync(target),
    restoreFavouriteAsync: (favouriteId: string, target: FavouriteTarget) =>
      restoreMutation.mutateAsync({ favouriteId, target }),

    isMutating:
      addMutation.isPending || removeMutation.isPending || restoreMutation.isPending,
  }
}
